import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { getToken } from "@/utils/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Lucide from "@/components/Base/Lucide";
import { FormInput, FormCheck, FormSelect } from "@/components/Base/Form";
import Button from "@/components/Base/Button";
import Table from "@/components/Base/Table";
import Pagination from "@/components/Base/Pagination";
import Alert from "@/components/Base/Alert";

type User = {
    id: string;
    firstName: string;
    lastName: string;
    username: string;
    mobileNo?: string;
    role?: { role: string };
    status: string;
};

type TeamMember = {
    id: string;
    teamId: string;
    userId: string;
    status: boolean;
    createdAt: string;
    updatedAt: string;
};

export default function AssignUser() {
    const { id: teamId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // State management
    const [users, setUsers] = useState<User[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
    const [existingMembers, setExistingMembers] = useState<Set<string>>(new Set());
    const [originalMembers, setOriginalMembers] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(true);
    const [assigning, setAssigning] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [sortField, setSortField] = useState<keyof User | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    // Get companyId from URL params
    const companyId = searchParams.get("companyId");

    // Load users and existing team members
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);

                // Fetch ALL users by paginating through all pages
                let allUsers: any[] = [];
                let currentPage = 1;
                let hasMorePages = true;
                const pageSize = 1000; // Fetch 1000 at a time

                while (hasMorePages) {
                    const queryParams = new URLSearchParams();
                    if (companyId) queryParams.append("companyId", companyId);
                    queryParams.append("pageSize", pageSize.toString());
                    queryParams.append("page", currentPage.toString());

                    const usersRes = await fetch(
                        `${import.meta.env.VITE_API_URL}/users?${queryParams.toString()}`,
                        {
                            headers: { Authorization: `Bearer ${getToken()}` }
                        }
                    );
                    const usersData = await usersRes.json();
                    const pageUsers = Array.isArray(usersData?.data) ? usersData.data : [];
                    
                    if (pageUsers.length > 0) {
                        allUsers = [...allUsers, ...pageUsers];
                        const totalPages = usersData.totalPages || 1;
                        hasMorePages = currentPage < totalPages;
                        currentPage++;
                    } else {
                        hasMorePages = false;
                    }
                }

                setUsers(allUsers);

                // Load existing team members
                const membersRes = await fetch(
                    `${import.meta.env.VITE_API_URL}/teams/${teamId}/members`,
                    {
                        headers: { Authorization: `Bearer ${getToken()}` }
                    }
                );
                const membersData = await membersRes.json();
                const memberUserIds: Set<string> = new Set(membersData?.data?.map((member: TeamMember) => member.userId) || []);
                setExistingMembers(memberUserIds);
                setOriginalMembers(memberUserIds);
                // Initialize selectedUsers with existing members so they appear checked
                setSelectedUsers(memberUserIds);

            } catch (error) {
                console.error("Failed to load data:", error);
                setMessage({ type: 'error', text: 'Failed to load data' });
            } finally {
                setLoading(false);
            }
        };

        if (teamId) {
            loadData();
        }
    }, [teamId, companyId]);

    // Handle individual user selection
    const handleUserSelect = (userId: string) => {
        setSelectedUsers(prev => {
            const newSet = new Set(prev);
            if (newSet.has(userId)) {
                newSet.delete(userId);
            } else {
                newSet.add(userId);
            }
            return newSet;
        });
    };

    // Handle select all
    const handleSelectAll = () => {
        const allUserIds = new Set(users.map(user => user.id));
        setSelectedUsers(allUserIds);
    };

    // Handle deselect all
    const handleDeselectAll = () => {
        setSelectedUsers(new Set());
    };

    // Handle column sorting
    const handleSort = (field: keyof User) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Assign/remove selected users to/from team
    const handleAssignUsers = async () => {
        // Check if there are any changes to make
        const usersToAdd = Array.from(selectedUsers).filter(userId => !originalMembers.has(userId));
        const usersToRemove = Array.from(originalMembers).filter(userId => !selectedUsers.has(userId));
        
        if (usersToAdd.length === 0 && usersToRemove.length === 0) {
            setMessage({ type: 'error', text: 'No changes to make' });
            return;
        }

        try {
            setAssigning(true);
            setMessage(null);

            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const token = getToken();

            const promises = [];

            // Add new team members
            if (usersToAdd.length > 0) {
                const addPromises = usersToAdd.map(userId =>
                fetch(`${apiUrl}/teams/${teamId}/members`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ userId, status: true })
                })
            );
                promises.push(...addPromises);
            }

            // Remove team members
            if (usersToRemove.length > 0) {
                const removePromises = usersToRemove.map(userId =>
                    fetch(`${apiUrl}/teams/${teamId}/members/${userId}`, {
                        method: 'DELETE',
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    })
                );
                promises.push(...removePromises);
            }

            await Promise.all(promises);

            const actionText = usersToAdd.length > 0 && usersToRemove.length > 0 
                ? `Successfully updated team: added ${usersToAdd.length} user(s), removed ${usersToRemove.length} user(s)`
                : usersToAdd.length > 0 
                    ? `Successfully added ${usersToAdd.length} user(s) to the team`
                    : `Successfully removed ${usersToRemove.length} user(s) from the team`;

            setMessage({ type: 'success', text: actionText });
            setRefreshing(true);

            // Refresh existing members
            const membersRes = await fetch(
                `${apiUrl}/teams/${teamId}/members`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const membersData = await membersRes.json();
            const memberUserIds: Set<string> = new Set(membersData?.data?.map((member: TeamMember) => member.userId) || []);
            setExistingMembers(memberUserIds);
            setOriginalMembers(memberUserIds);
            // Update selectedUsers to reflect the new state (all current members should be checked)
            setSelectedUsers(memberUserIds);
            setRefreshing(false);

        } catch (error) {
            console.error("Failed to update team members:", error);
            setMessage({ type: 'error', text: 'Failed to update team members' });
        } finally {
            setAssigning(false);
        }
    };

    // Filter and sort users
    const filteredAndSortedUsers = users
        .filter(user =>
        user.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.username.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .sort((a, b) => {
            if (!sortField) return 0;
            
            const aValue = a[sortField];
            const bValue = b[sortField];
            
            // Handle nested objects (like role)
            const aStr = typeof aValue === 'object' ? aValue?.role || '' : String(aValue || '');
            const bStr = typeof bValue === 'object' ? bValue?.role || '' : String(bValue || '');
            
            if (sortDirection === 'asc') {
                return aStr.localeCompare(bStr);
            } else {
                return bStr.localeCompare(aStr);
            }
        });

    // Calculate pagination
    const totalFiltered = filteredAndSortedUsers.length;
    const calculatedTotalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedUsers = filteredAndSortedUsers.slice(startIndex, endIndex);

    // Update totalPages and total when filtered results change
    React.useEffect(() => {
        setTotalPages(calculatedTotalPages);
        setTotal(totalFiltered);
        // Reset to page 1 if current page is beyond available pages
        if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
            setCurrentPage(1);
        }
    }, [totalFiltered, pageSize, calculatedTotalPages]);

    // Pagination handlers
    const handleFirstPage = () => setCurrentPage(1);
    const handlePrevPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };
    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };
    const handleLastPage = () => setCurrentPage(totalPages);
    const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <Lucide icon="Loader2" className="w-8 h-8 animate-spin mx-auto mb-2" />
                    <div>Loading users...</div>
                </div>
            </div>
        );
    }

    if (refreshing) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <Lucide icon="Loader2" className="w-8 h-8 animate-spin mx-auto mb-2" />
                    <div>Updating team members...</div>
                </div>
            </div>
        );
    }

    return (
        <ProtectedRoute>
            <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
                <div className="grid grid-cols-12 gap-y-10 gap-x-6 max-w-7xl mx-auto">
                    <div className="col-span-12">
                        {/* Header */}
                        <div className="flex flex-col mt-4 md:mt-0 md:h-10 gap-y-3 md:items-center md:flex-row">
                            <div className="text-base font-medium group-[.mode--light]:text-white">
                                Assign Users to Team
                            </div>
                            <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 md:ml-auto">
                                <Button
                                    variant="outline-secondary"
                                    onClick={handleSelectAll}
                                    disabled={assigning}
                                >
                                    <Lucide icon="CheckSquare" className="stroke-[1.3] w-4 h-4 mr-2" />
                                    Select All
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    onClick={handleDeselectAll}
                                    disabled={assigning}
                                >
                                    <Lucide icon="Square" className="stroke-[1.3] w-4 h-4 mr-2" />
                                    Deselect All
                                </Button>
                            </div>
                        </div>


                        {/* Search and Table */}
                        <div className="mt-3.5">
                            <div className="flex flex-col box box--stacked max-w-full overflow-hidden">
                                <div className="flex flex-col p-5 sm:items-center sm:flex-row gap-y-2">
                                    <div className="flex flex-col sm:flex-row gap-y-2 sm:gap-x-4 sm:items-center w-full">
                                        <div className="relative">
                                            <Lucide
                                                icon="Search"
                                                className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                                            />
                                            <FormInput
                                                type="text"
                                                placeholder="Search users..."
                                                className="pl-9 sm:w-64 rounded-[0.5rem]"
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                            />
                                        </div>
                                        {message && (
                                            <div className={`text-sm px-3 py-2 rounded-md flex items-center sm:ml-auto ${
                                                message.type === 'success' 
                                                    ? 'bg-success/10 text-success border border-success/20' 
                                                    : 'bg-danger/10 text-danger border border-danger/20'
                                            }`}>
                                                <Lucide 
                                                    icon={message.type === 'success' ? 'CheckCircle' : 'AlertTriangle'} 
                                                    className="w-4 h-4 mr-2" 
                                                />
                                                {message.text}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="overflow-auto xl:overflow-visible">
                                    <Table className="border-b border-slate-200/60 text-xs">
                                        <Table.Thead className="text-xs">
                                            <Table.Tr>
                                                <Table.Td 
                                                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none"
                                                    onClick={() => handleSort('firstName')}
                                                >
                                                    <div className="flex items-center gap-1 py-1.5">
                                                        First Name
                                                        {sortField === 'firstName' && (
                                                            <Lucide 
                                                                icon={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                                                                className="w-3 h-3" 
                                                            />
                                                        )}
                                                    </div>
                                                </Table.Td>
                                                <Table.Td 
                                                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none"
                                                    onClick={() => handleSort('lastName')}
                                                >
                                                    <div className="flex items-center gap-1 py-1.5">
                                                        Last Name
                                                        {sortField === 'lastName' && (
                                                            <Lucide 
                                                                icon={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                                                                className="w-3 h-3" 
                                                            />
                                                        )}
                                                    </div>
                                                </Table.Td>
                                                <Table.Td 
                                                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none"
                                                    onClick={() => handleSort('username')}
                                                >
                                                    <div className="flex items-center gap-1 py-1.5">
                                                        Username
                                                        {sortField === 'username' && (
                                                            <Lucide 
                                                                icon={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                                                                className="w-3 h-3" 
                                                            />
                                                        )}
                                                    </div>
                                                </Table.Td>
                                                <Table.Td 
                                                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none"
                                                    onClick={() => handleSort('mobileNo')}
                                                >
                                                    <div className="flex items-center gap-1 py-1.5">
                                                        Mobile No
                                                        {sortField === 'mobileNo' && (
                                                            <Lucide 
                                                                icon={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                                                                className="w-3 h-3" 
                                                            />
                                                        )}
                                                    </div>
                                                </Table.Td>
                                                <Table.Td 
                                                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none"
                                                    onClick={() => handleSort('role')}
                                                >
                                                    <div className="flex items-center gap-1 py-1.5">
                                                        Role
                                                        {sortField === 'role' && (
                                                            <Lucide 
                                                                icon={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                                                                className="w-3 h-3" 
                                                            />
                                                        )}
                                                    </div>
                                                </Table.Td>
                                                <Table.Td 
                                                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none"
                                                    onClick={() => handleSort('status')}
                                                >
                                                    <div className="flex items-center gap-1 py-1.5">
                                                        Status
                                                        {sortField === 'status' && (
                                                            <Lucide 
                                                                icon={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                                                                className="w-3 h-3" 
                                                            />
                                                        )}
                                                    </div>
                                                </Table.Td>
                                                <Table.Td className="text-center py-1.5">Assign</Table.Td>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {paginatedUsers.map((user) => (
                                                <Table.Tr key={user.id} className="[&_td]:last:border-b-0">
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {user.firstName}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {user.lastName}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {user.username}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {user.mobileNo || "—"}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {user.role?.role || "—"}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        <span className={`px-1.5 py-0.5 text-[10px] rounded-full ${user.status ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                                                            }`}>
                                                            {user.status ? "Active" : "Inactive"}
                                                        </span>
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 text-center border-dashed dark:bg-darkmode-600 text-xs">
                                                        <div className="flex justify-center items-center">
                                                            <FormCheck>
                                                                <FormCheck.Input
                                                                    type="checkbox"
                                                                    checked={selectedUsers.has(user.id)}
                                                                    onChange={() => handleUserSelect(user.id)}
                                                                    disabled={assigning}
                                                                    className="scale-90"
                                                                />
                                                            </FormCheck>
                                                        </div>
                                                    </Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>
                                    </Table>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex justify-between items-center p-5 border-t border-slate-200/60">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => navigate(-1)}
                                        disabled={assigning}
                                    >
                                        <Lucide icon="ArrowLeft" className="w-4 h-4 mr-2" />
                                        Back
                                    </Button>
                                    <div className="flex gap-3">
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => navigate(-1)}
                                            disabled={assigning}
                                        >
                                            <Lucide icon="X" className="w-4 h-4 mr-2" />
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={handleAssignUsers}
                                            disabled={assigning}
                                        >
                                            {assigning && <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />}
                                            <Lucide icon="Users" className="w-4 h-4 mr-2" />
                                            Update Team
                                        </Button>
                                    </div>
                                </div>

                                {/* Pagination */}
                                <div className="flex flex-col-reverse flex-wrap items-center p-5 flex-reverse gap-y-2 sm:flex-row">
                                    <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                                        Showing {totalFiltered > 0 ? startIndex + 1 : 0} to {Math.min(endIndex, totalFiltered)} of {totalFiltered} users
                                    </div>
                                    <Pagination className="flex-1 w-full mr-auto sm:w-auto">
                                        <Pagination.Link
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleFirstPage();
                                            }}
                                            className={currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronsLeft" className="w-4 h-4" />
                                        </Pagination.Link>
                                        <Pagination.Link
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handlePrevPage();
                                            }}
                                            className={currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronLeft" className="w-4 h-4" />
                                        </Pagination.Link>
                                        {totalPages > 0 && (
                                            <>
                                                {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
                                                    let pageNum: number;
                                                    if (totalPages <= 5) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage <= 3) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage >= totalPages - 2) {
                                                        pageNum = totalPages - 4 + i;
                                                    } else {
                                                        pageNum = currentPage - 2 + i;
                                                    }
                                                    return (
                                                        <Pagination.Link
                                                            key={pageNum}
                                                            active={currentPage === pageNum}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                setCurrentPage(pageNum);
                                                            }}
                                                            className="cursor-pointer"
                                                        >
                                                            {pageNum}
                                                        </Pagination.Link>
                                                    );
                                                })}
                                            </>
                                        )}
                                        <Pagination.Link
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleNextPage();
                                            }}
                                            className={currentPage >= totalPages ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronRight" className="w-4 h-4" />
                                        </Pagination.Link>
                                        <Pagination.Link
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleLastPage();
                                            }}
                                            className={currentPage >= totalPages ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronsRight" className="w-4 h-4" />
                                        </Pagination.Link>
                                    </Pagination>
                                    <FormSelect 
                                        className="sm:w-20 rounded-[0.5rem]"
                                        value={pageSize}
                                        onChange={handlePageSizeChange}
                                    >
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                        <option value={250}>250</option>
                                        <option value={500}>500</option>
                                    </FormSelect>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </RoleGate>
        </ProtectedRoute>
    );
}